import { useEffect, useRef } from "react";

// Charge l'API IFrame YouTube une seule fois (singleton)
let _ytPromise = null;
function loadYT() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (_ytPromise) return _ytPromise;
  _ytPromise = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { if (prev) prev(); resolve(window.YT); };
    document.head.appendChild(tag);
  });
  return _ytPromise;
}

const DRIFT = 1.4; // secondes de tolérance avant re-synchro

/**
 * Lecteur vidéo synchronisé.
 * props:
 *  - state: {source, url, youtube_id, state:'playing'|'paused', position, rate}
 *  - mode: 'host' (contrôles interactifs) | 'viewer' (synchronisé, lecture seule)
 *  - onControl(action, position): émis en mode host (play|pause|seek|stop)
 */
export function LiveVideoPlayer({ state, mode = "viewer", onControl }) {
  const isHost = mode === "host";
  const ytHostRef = useRef(null);      // div id container YT
  const ytPlayer = useRef(null);
  const videoRef = useRef(null);       // <video> pour fichier
  const lastSig = useRef("");          // signature du dernier state appliqué (viewer)
  const guardIv = useRef(null);        // interval host: maintien de synchro pendant lecture

  const source = state?.source;
  const ytId = state?.youtube_id;

  // ---------- YOUTUBE ----------
  useEffect(() => {
    if (source !== "youtube" || !ytId) return;
    let cancelled = false;
    loadYT().then((YT) => {
      if (cancelled || !ytHostRef.current) return;
      if (ytPlayer.current) { try { ytPlayer.current.destroy(); } catch {} ytPlayer.current = null; }
      ytPlayer.current = new YT.Player(ytHostRef.current, {
        videoId: ytId,
        playerVars: {
          controls: isHost ? 1 : 0, disablekb: isHost ? 0 : 1, modestbranding: 1,
          rel: 0, playsinline: 1, fs: isHost ? 1 : 0,
        },
        events: {
          onReady: () => { applyViewerSync(true); },
          onStateChange: (e) => {
            if (!isHost || !ytPlayer.current) return;
            const S = window.YT.PlayerState;
            const t = ytPlayer.current.getCurrentTime?.() || 0;
            if (e.data === S.PLAYING) { onControl?.("play", t); startGuard(); }
            else if (e.data === S.PAUSED) { onControl?.("pause", t); stopGuard(); }
            else if (e.data === S.ENDED) { onControl?.("stop", 0); stopGuard(); }
          },
        },
      });
    });
    return () => { cancelled = true; stopGuard(); if (ytPlayer.current) { try { ytPlayer.current.destroy(); } catch {} ytPlayer.current = null; } };
    // eslint-disable-line react-hooks/exhaustive-deps
  }, [source, ytId, isHost]);

  const startGuard = () => {
    if (!isHost || guardIv.current) return;
    guardIv.current = setInterval(() => {
      const t = ytPlayer.current?.getCurrentTime?.();
      if (typeof t === "number") onControl?.("play", t); // re-cale l'extrapolation serveur
    }, 5000);
  };
  const stopGuard = () => { if (guardIv.current) { clearInterval(guardIv.current); guardIv.current = null; } };

  // Synchronisation du viewer sur le state serveur (YouTube)
  const applyViewerSync = (force = false) => {
    if (isHost || source !== "youtube" || !ytPlayer.current) return;
    const p = ytPlayer.current;
    try {
      const cur = p.getCurrentTime?.() || 0;
      if (force || Math.abs(cur - (state.position || 0)) > DRIFT) p.seekTo(state.position || 0, true);
      if (state.state === "playing") p.playVideo?.(); else p.pauseVideo?.();
    } catch {}
  };

  useEffect(() => {
    if (isHost || source !== "youtube") return;
    const sig = `${state.state}:${Math.round(state.position)}`;
    if (sig === lastSig.current) return;
    lastSig.current = sig;
    applyViewerSync(false);
    // eslint-disable-line react-hooks/exhaustive-deps
  }, [state, source, isHost]);

  // ---------- FICHIER MP4 ----------
  useEffect(() => {
    if (source !== "file" || isHost || !videoRef.current) return;
    const v = videoRef.current;
    try {
      if (Math.abs(v.currentTime - (state.position || 0)) > DRIFT) v.currentTime = state.position || 0;
      if (state.state === "playing") { const pr = v.play(); if (pr) pr.catch(() => {}); }
      else v.pause();
    } catch {}
  }, [state, source, isHost]);

  const fileHandlers = isHost ? {
    onPlay: () => onControl?.("play", videoRef.current?.currentTime || 0),
    onPause: () => onControl?.("pause", videoRef.current?.currentTime || 0),
    onSeeked: () => onControl?.("seek", videoRef.current?.currentTime || 0),
    onEnded: () => onControl?.("stop", 0),
  } : {};

  if (!source) return null;

  return (
    <div className="relative overflow-hidden rounded-2xl bg-black" data-testid="live-video-player">
      <div className="relative aspect-video w-full">
        {source === "youtube" ? (
          <>
            <div ref={ytHostRef} className="absolute inset-0 h-full w-full" data-testid="live-video-youtube" />
            {!isHost && <div className="absolute inset-0 z-10 cursor-not-allowed" title="Lecture pilotée par l'intervenant" data-testid="live-video-overlay" />}
          </>
        ) : (
          <video ref={videoRef} src={state.url} controls={isHost} playsInline data-testid="live-video-file"
            className="absolute inset-0 h-full w-full" />
        )}
      </div>
      {!isHost && (
        <div className="flex items-center gap-2 bg-black/80 px-3 py-1.5 text-[11px] text-white/80">
          <span className={`h-2 w-2 rounded-full ${state.state === "playing" ? "animate-pulse bg-emerald-400" : "bg-slate-400"}`} />
          {state.state === "playing" ? "En lecture — synchronisé avec l'intervenant" : "En pause — piloté par l'intervenant"}
        </div>
      )}
    </div>
  );
}
